<script setup>
import { computed, ref, watch } from 'vue';
import {
  DEFAULT_BARS,
  cachedPeaks,
  decorativePeaks,
  loadPeaks,
} from './waveform';

const props = defineProps({
  src: { type: String, default: '' },
  seed: { type: [String, Number], default: '' },
  currentTime: { type: Number, default: 0 },
  duration: { type: Number, default: 0 },
  // real peaks are only decoded once the user has engaged with the audio
  active: { type: Boolean, default: false },
  bars: { type: Number, default: DEFAULT_BARS },
});

const emit = defineEmits(['seek']);

const realPeaks = ref(cachedPeaks(props.src));
const isDecoding = ref(false);

const peaks = computed(
  () => realPeaks.value || decorativePeaks(props.seed || props.src, props.bars)
);

const progress = computed(() => {
  if (!props.duration || !Number.isFinite(props.duration)) return 0;
  return Math.min(1, Math.max(0, props.currentTime / props.duration));
});

const filledBars = computed(() =>
  Math.round(progress.value * peaks.value.length)
);

const decode = async () => {
  if (realPeaks.value || isDecoding.value || !props.src) return;
  isDecoding.value = true;
  realPeaks.value = await loadPeaks(props.src, props.bars);
  isDecoding.value = false;
};

watch(
  () => props.active,
  active => {
    if (active) decode();
  },
  { immediate: true }
);

const seekFromEvent = event => {
  if (!props.duration || !Number.isFinite(props.duration)) return;
  const rect = event.currentTarget.getBoundingClientRect();
  const ratio = Math.min(
    1,
    Math.max(0, (event.clientX - rect.left) / rect.width)
  );
  emit('seek', ratio * props.duration);
};

const onKeydown = event => {
  if (!props.duration) return;
  const step = Math.max(1, props.duration / 20);
  if (event.key === 'ArrowRight') {
    emit('seek', Math.min(props.duration, props.currentTime + step));
    event.preventDefault();
  } else if (event.key === 'ArrowLeft') {
    emit('seek', Math.max(0, props.currentTime - step));
    event.preventDefault();
  }
};
</script>

<template>
  <div
    role="slider"
    tabindex="0"
    :aria-valuemin="0"
    :aria-valuemax="Math.round(duration || 0)"
    :aria-valuenow="Math.round(currentTime || 0)"
    class="flex items-center w-full h-8 gap-px cursor-pointer select-none outline-none focus-visible:ring-1 focus-visible:ring-n-brand rounded"
    :class="{ 'animate-pulse': isDecoding }"
    @click="seekFromEvent"
    @keydown="onKeydown"
  >
    <span
      v-for="(peak, index) in peaks"
      :key="index"
      class="flex-1 min-w-px rounded-full transition-colors duration-150"
      :class="index < filledBars ? 'bg-current' : 'bg-n-slate-12/25'"
      :style="{ height: `${Math.round(peak * 100)}%` }"
    />
  </div>
</template>
